import {
  AFTER,
  BEFORE,
  CONTAINS,
  EQ,
  GT,
  GTE,
  LESS_THAN,
  LT,
  LTE,
  MORE_THAN,
  NOT,
  NOT_CONTAINS,
  NOT_ONE_OF,
  ONE_OF,
  type Operator,
} from './Operators';

// these should match EnumColumnType.java
export const BOOL = 'BOOL';
export const INT = 'INT';
export const FLOAT = 'FLOAT';
export const DATE = 'DATE';
export const DATETIME = 'DATETIME';
export const STRING = 'STRING';
export const INTERVAL = 'INTERVAL';
export const UUID = 'UUID';
export const JSON = 'JSON';
export const STRUCT = 'STRUCT';
export const ARRAY = 'ARRAY';
export const OTHER = 'OTHER';

export type ColumnType =
  | typeof BOOL
  | typeof INT
  | typeof FLOAT
  | typeof DATE
  | typeof DATETIME
  | typeof STRING
  | typeof UUID
  | typeof INTERVAL
  | typeof JSON
  | typeof STRUCT
  | typeof ARRAY
  | typeof OTHER;

export type OperatorAwareColumnType =
  | typeof BOOL
  | typeof INT
  | typeof FLOAT
  | typeof DATE
  | typeof DATETIME
  | typeof STRING
  | typeof UUID
  | typeof JSON;

export type MethodAwareColumnType =
  | typeof INT
  | typeof FLOAT
  | typeof DATETIME
  | typeof STRING;

// NOTE: all types should at least have EQ and NOT (as their intersection is what is shown by default in WHERE Modal)
const operatorsByType: Record<OperatorAwareColumnType, Operator[]> = {
  [BOOL]: [EQ, NOT],
  [INT]: [EQ, NOT, LT, LTE, GT, GTE, ONE_OF, NOT_ONE_OF],
  [FLOAT]: [EQ, NOT, LT, LTE, GT, GTE],
  [DATETIME]: [LESS_THAN, MORE_THAN, BEFORE, AFTER],
  [DATE]: [EQ, NOT, LESS_THAN, MORE_THAN, BEFORE, AFTER],
  [STRING]: [EQ, NOT, CONTAINS, NOT_CONTAINS, ONE_OF, NOT_ONE_OF],
  [UUID]: [EQ, NOT, ONE_OF, NOT_ONE_OF, CONTAINS, NOT_CONTAINS],
  [JSON]: [CONTAINS, NOT_CONTAINS, EQ, NOT],
};
// NOTE: not including JSON or ARRAY here, because they don't support comparison operators (even though jsonb does a little bit)

// methods
// eslint-disable-next-line no-template-curly-in-string
const M_STRING = '"hello ${column}"';
const M_ADDITION = 'column + 1';
const M_REGEX_EXTRACT = 'column.extract(".(.*)")';
const M_DAY = 'column.day()';
const M_WEEK = 'column.week()';
const M_MONTH = 'column.month()';
const M_YEAR = 'column.year()';

export type Method =
  | typeof M_STRING
  | typeof M_ADDITION
  | typeof M_REGEX_EXTRACT
  | typeof M_DAY
  | typeof M_WEEK
  | typeof M_MONTH
  | typeof M_YEAR;

const methodsByType: Record<MethodAwareColumnType, Method[]> = {
  [DATETIME]: [M_STRING, M_DAY, M_WEEK, M_MONTH, M_YEAR],
  [STRING]: [M_STRING, M_REGEX_EXTRACT],
  [INT]: [M_ADDITION, M_STRING],
  [FLOAT]: [M_ADDITION, M_STRING],
};

class ColumnTypeData {
  constructor(readonly _type: ColumnType, readonly _nullable: boolean) {}

  static build(typeFromServer: ColumnTypeData) {
    return new ColumnTypeData(typeFromServer._type, typeFromServer._nullable);
  }

  type() {
    return this._type;
  }

  operators() {
    // TODO: model it in a more safe way.
    return operatorsByType[this.type() as OperatorAwareColumnType];
  }

  methods() {
    const result = methodsByType[this.type() as MethodAwareColumnType];
    if (!result) {
      return [M_STRING];
    }
    return result;
  }

  nullable() {
    return this._nullable;
  }
}

export default ColumnTypeData;
